import {
  Cell,
  Column,
  ColumnDef,
  ColumnResizeMode,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  Header,
  useReactTable,
  type PaginationState,
  type Row,
  type RowData,
  type TableOptions,
  type Table as TableType,
} from '@tanstack/react-table';
import type { CSSProperties } from 'react';

// DnD Kit imports
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  Fragment,
  Ref,
  useCallback,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { Checkbox } from '../ui/checkbox';
import { FlexRow } from '../ui/flex';
import { Skeleton } from '../ui/skeleton';
import { Text } from '../ui/text';
import { CustomScrollbar } from './CustomScrollbar';
import './DataTable.css';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateCell?: (
      rowId: string,
      columnId: string,
      value: unknown,
      columnType: string
    ) => void;
    updateData?: (rowIndex: number, columnId: string, value: unknown) => void;
  }
}

/**
 * Helper function for sticky column pinning styles.
 * Apply to header cells, data cells, and footer cells.
 * @see https://tanstack.com/table/latest/docs/framework/react/examples/column-pinning-sticky
 */
const getCommonPinningStyles = <TData,>(
  column: Column<TData, unknown>
): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn =
    isPinned === 'right' && column.getIsFirstColumn('right');
  const useStickyBorder =
    isLastLeftPinnedColumn && column.columnDef.meta?.stickyLastLeftBorder2;

  return {
    boxShadow: useStickyBorder
      ? undefined
      : isLastLeftPinnedColumn
        ? '0 0 4px -4px gray inset'
        : isFirstRightPinnedColumn
          ? '0 0 4px -4px gray inset'
          : undefined,
    borderRightWidth: useStickyBorder ? 2 : undefined,
    borderRightStyle: useStickyBorder ? 'solid' : undefined,
    borderRightColor: useStickyBorder ? 'rgb(229 229 229)' : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

/**
 * DraggableTableHeader component for column reordering
 * Handles the drag and drop functionality for table headers
 */
const DraggableTableHeader = <TData,>({
  header,
  enableColumnResizing,
  hasColumnPinning,
  nonDraggableColumnIds,
}: {
  header: Header<TData, unknown>;
  enableColumnResizing: boolean;
  hasColumnPinning: boolean;
  nonDraggableColumnIds: string[];
}) => {
  const { column } = header;
  const isPinned = column.getIsPinned();
  const columnId = column.id;

  // Pinned columns and specified non-draggable columns should not be draggable
  const isNonDraggable = !!isPinned || nonDraggableColumnIds.includes(columnId);

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
      disabled: isNonDraggable,
    });

  const pinningStyles = hasColumnPinning ? getCommonPinningStyles(column) : {};
  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: 'sticky',
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s ease-in-out',
    whiteSpace: 'nowrap',
    width: enableColumnResizing ? header.getSize() : undefined,
    zIndex: isDragging ? 1 : hasColumnPinning && isPinned ? 3 : 2,
    ...pinningStyles,
    top: 0,
  };

  // Modify listeners to check if we're clicking on a drag handle
  const modifiedListeners = isNonDraggable
    ? {}
    : Object.entries(listeners || {}).reduce(
        (acc, [key, value]) => {
          acc[key] = (event: any) => {
            // Check if the event target has data-drag-handle or is within it
            const target = event.target as HTMLElement;
            const dragHandle = target.closest('[data-drag-handle="true"]');

            // Only allow drag if clicking on the drag handle area
            if (dragHandle) {
              value(event);
            }
          };
          return acc;
        },
        {} as Record<string, any>
      );

  return (
    <TableHead
      ref={setNodeRef}
      style={style}
      className={cn(header.column.columnDef.meta?.headerClassName)}
      {...attributes}
      {...modifiedListeners}
    >
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
      {enableColumnResizing && header.column.getCanResize() && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={cn(
            'column-resizer',
            header.column.getIsResizing() && 'isResizing'
          )}
        />
      )}
    </TableHead>
  );
};

/**
 * DragAlongCell component that follows the header during drag
 * Ensures cells stay aligned with their headers during reordering
 */
const DragAlongCell = <TData,>({
  cell,
  enableColumnResizing,
  hasColumnPinning,
  nonDraggableColumnIds,
}: {
  cell: Cell<TData, unknown>;
  enableColumnResizing: boolean;
  hasColumnPinning: boolean;
  nonDraggableColumnIds: string[];
}) => {
  const { column } = cell;
  const isPinned = column.getIsPinned();
  const columnId = column.id;

  // Pinned columns and specified non-draggable columns should not be draggable
  const isNonDraggable = !!isPinned || nonDraggableColumnIds.includes(columnId);

  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
    disabled: isNonDraggable,
  });

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: hasColumnPinning && isPinned ? 'sticky' : 'relative',
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s ease-in-out',
    width: enableColumnResizing ? cell.column.getSize() : undefined,
    zIndex: isDragging ? 1 : hasColumnPinning && isPinned ? 1 : 0,
    ...(hasColumnPinning ? getCommonPinningStyles(column) : {}),
  };

  return (
    <TableCell
      ref={setNodeRef}
      style={style}
      className={cn(cell.column.columnDef.meta?.cellClassName)}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  );
};

const tableVariants = cva('', {
  variants: {
    headerStyle: {
      default: '',
      darker: 'data-table-darker-header',
    },
    grid: {
      default: '',
      bordered: 'data-table-grid',
    },
  },
  defaultVariants: {
    headerStyle: 'default',
    grid: 'default',
  },
});

interface DataTableProps<TData, TValue>
  extends VariantProps<typeof tableVariants> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pagination?: PaginationType;
  options?: Options<TData>;
  rowCount?: number;
  isLoading?: boolean;
  className?: string;
  onRowClick?: (row: TData) => void;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  renderSubComponent?: (row: Row<TData>) => React.ReactNode;
  displayTopPagination?: boolean;
  hidePagination?: boolean;
  enableColumnResizing?: boolean;
  columnResizeMode?: ColumnResizeMode;
  enableColumnReordering?: boolean;
  onColumnOrderChange?: (columnOrder: string[]) => void;
  nonDraggableColumnIds?: string[];
  /** When true, allow horizontal scroll (e.g. wide tables with sticky columns). */
  scrollX?: boolean;
  /** When true, the custom horizontal scrollbar sticks to the top of the scroll container. */
  stickyScrollbar?: boolean;
  /** Optional ref to the scrollable table viewport (for scroll position persistence). */
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
}

export type PaginationType = {
  state: PaginationState;
  onPaginationChange: TableOptions<any>['onPaginationChange'];
  manualPagination?: boolean;
};

export type Options<TData> = Omit<
  TableOptions<TData>,
  'columns' | 'data' | 'getCoreRowModel'
>;

export function DataTable<TData, TValue>({
  columns,
  data,
  pagination,
  options = {},
  rowCount,
  isLoading,
  className,
  onRowClick,
  headerStyle,
  grid,
  getRowCanExpand,
  renderSubComponent,
  displayTopPagination = false,
  hidePagination = false,
  enableColumnResizing = false,
  columnResizeMode = 'onChange',
  enableColumnReordering = false,
  onColumnOrderChange,
  nonDraggableColumnIds = [],
  scrollX = false,
  stickyScrollbar = false,
  scrollContainerRef,
}: DataTableProps<TData, TValue>) {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Helper function to check if a column is non-draggable
  // Memoize to prevent recreation on every render
  const isColumnNonDraggable = useCallback(
    (columnId: string) => {
      return nonDraggableColumnIds.includes(columnId);
    },
    [nonDraggableColumnIds]
  );

  // Initialize column order, ensuring non-draggable columns are at the end
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const allColumnIds = columns.map(
      c => (c.id ?? (c as any).accessorKey ?? '') as string
    );

    // Separate draggable from non-draggable columns
    const draggableColumns = allColumnIds.filter(
      id => !isColumnNonDraggable(id)
    );
    const nonDraggable = allColumnIds.filter(id => isColumnNonDraggable(id));

    // Return draggable first, then non-draggable at the end
    return [...draggableColumns, ...nonDraggable];
  });

  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Sync column order synchronously during render when columns change
  // This avoids the delay caused by useEffect (which runs after paint)
  const columnIdsKey = columns
    .map(c => (c.id ?? (c as any).accessorKey ?? '') as string)
    .join(',');
  const prevColumnIdsKeyRef = useRef(columnIdsKey);

  if (enableColumnReordering && columnIdsKey !== prevColumnIdsKeyRef.current) {
    prevColumnIdsKeyRef.current = columnIdsKey;

    const allColumnIds = columns.map(
      c => (c.id ?? (c as any).accessorKey ?? '') as string
    );
    const draggableColumns = allColumnIds.filter(
      id => !isColumnNonDraggable(id)
    );
    const nonDraggable = allColumnIds.filter(id => isColumnNonDraggable(id));

    const existingDraggable = columnOrder.filter(
      id => !isColumnNonDraggable(id)
    );
    const existingSet = new Set(existingDraggable);
    const newColumns = draggableColumns.filter(id => !existingSet.has(id));

    let updatedOrder: string[];
    if (
      newColumns.length === 0 &&
      existingDraggable.length === draggableColumns.length
    ) {
      // Same columns, different order — use the new order directly
      updatedOrder = [...draggableColumns, ...nonDraggable];
    } else {
      // Columns added/removed — preserve existing order, append new
      updatedOrder = [
        ...existingDraggable.filter(id => allColumnIds.includes(id)),
        ...newColumns,
        ...nonDraggable,
      ];
    }

    // Only update if actually different
    if (
      columnOrder.some((id, idx) => id !== updatedOrder[idx]) ||
      columnOrder.length !== updatedOrder.length
    ) {
      setColumnOrder(updatedOrder);
    }
  }

  const tableOptions: TableOptions<TData> = useMemo(() => {
    const { state: optsState, ...optionsProps } = options;
    let state = optsState;
    let paginationOpts;

    if (pagination) {
      const {
        manualPagination: manualPaginationState,
        state: paginationState,
        onPaginationChange,
      } = pagination;
      state = { ...(state ?? {}), pagination: paginationState };
      paginationOpts = {
        manualPagination: manualPaginationState ?? true,
        onPaginationChange,
      };
    }

    if (getRowCanExpand) {
      state = { ...state, expanded };
    }

    if (enableColumnReordering) {
      state = { ...state, columnOrder };
    }

    const opts: TableOptions<TData> = {
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      ...(state ? { state } : {}),
      ...(paginationOpts ?? {}),
      rowCount,
      ...optionsProps,
      ...(getRowCanExpand ? { getRowCanExpand } : {}),
      ...(getRowCanExpand ? { onExpandedChange: setExpanded } : {}),
      ...(getRowCanExpand
        ? { getExpandedRowModel: getExpandedRowModel() }
        : {}),
      ...(enableColumnResizing
        ? { enableColumnResizing: true, columnResizeMode }
        : {}),
      ...(enableColumnReordering
        ? { onColumnOrderChange: setColumnOrder }
        : {}),
    };
    return opts;
  }, [
    data,
    columns,
    getCoreRowModel,
    pagination,
    rowCount,
    options,
    getRowCanExpand,
    expanded,
    enableColumnResizing,
    columnResizeMode,
    enableColumnReordering,
    columnOrder,
  ]);

  const table = useReactTable(tableOptions);

  // Setup DnD sensors for column reordering
  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  // Handle column reordering after drag & drop
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const activeId = active.id as string;
      const overId = over.id as string;

      // Prevent reordering if either column is non-draggable
      if (isColumnNonDraggable(activeId) || isColumnNonDraggable(overId)) {
        return;
      }

      setColumnOrder(columnOrder => {
        // Separate draggable from non-draggable columns
        const draggableColumns = columnOrder.filter(
          id => !isColumnNonDraggable(id)
        );
        const nonDraggable = columnOrder.filter(id => isColumnNonDraggable(id));

        // Reorder only the draggable columns
        const oldIndex = draggableColumns.indexOf(activeId);
        const newIndex = draggableColumns.indexOf(overId);
        const reorderedDraggable = arrayMove(
          draggableColumns,
          oldIndex,
          newIndex
        );

        // Combine: draggable columns first, then non-draggable at the end
        const newOrder = [...reorderedDraggable, ...nonDraggable];

        // Call the optional callback to notify parent component
        onColumnOrderChange?.(newOrder);

        return newOrder;
      });
    }
  };

  // Check if any column is pinned (left or right)
  const hasColumnPinning = table.getIsSomeColumnsPinned();

  const isAllRowsSelected = table.getIsAllRowsSelected();
  const selectedRowsCount = options.state?.rowSelection
    ? Object.values(options.state.rowSelection).filter(Boolean).length
    : 0;

  const totalRows = table.getRowCount();

  // Wrap table rendering based on whether column reordering is enabled
  const tableContent = (
    <div
      ref={node => {
        tableScrollRef.current = node;
        if (scrollContainerRef) {
          scrollContainerRef.current = node;
        }
      }}
      className={cn(
        'rounded-md border data-table-scroll min-w-0 flex-1 min-h-0',
        scrollX ? 'overflow-auto' : 'no-scrollbar-x overflow-y-auto',
        tableVariants({ headerStyle, grid }),
        enableColumnResizing && 'data-table-resizable',
        hasColumnPinning && 'data-table-column-pinning'
      )}
    >
      <Table
        style={
          enableColumnResizing
            ? { width: table.getCenterTotalSize() }
            : undefined
        }
      >
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {enableColumnReordering ? (
                <SortableContext
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map(header => (
                    <DraggableTableHeader
                      key={header.id}
                      header={header}
                      enableColumnResizing={enableColumnResizing}
                      hasColumnPinning={hasColumnPinning}
                      nonDraggableColumnIds={nonDraggableColumnIds}
                    />
                  ))}
                </SortableContext>
              ) : (
                headerGroup.headers.map(header => {
                  const { column } = header;
                  const pinningStyles = hasColumnPinning
                    ? getCommonPinningStyles(column)
                    : {};
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        header.column.columnDef.meta?.headerClassName
                      )}
                      style={{
                        ...(enableColumnResizing
                          ? { width: header.getSize() }
                          : {}),
                        ...pinningStyles,
                        position: 'sticky',
                        top: 0,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {enableColumnResizing && header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={cn(
                            'column-resizer',
                            header.column.getIsResizing() && 'isResizing'
                          )}
                        />
                      )}
                    </TableHead>
                  );
                })
              )}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({
              length: table.getState().pagination.pageSize,
            }).map((_, i) => (
              <TableRow key={i}>
                {table.getHeaderGroups().map(headerGroup =>
                  headerGroup.headers.map((header, idx) => {
                    const { column } = header;
                    return (
                      <TableCell
                        className={cn(
                          header.column.columnDef.meta?.cellClassName ?? ''
                        )}
                        key={idx}
                        style={{
                          ...(enableColumnResizing
                            ? { width: header.column.getSize() }
                            : {}),
                          ...(hasColumnPinning
                            ? getCommonPinningStyles(column)
                            : {}),
                        }}
                      >
                        <Skeleton className="rounded-xl h-4 my-1 w-full" />
                      </TableCell>
                    );
                  })
                )}
              </TableRow>
            ))
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map(row => (
              <Fragment key={row.id}>
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={onRowClick ? 'cursor-pointer' : ''}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {enableColumnReordering ? (
                    <SortableContext
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {row.getVisibleCells().map(cell => (
                        <DragAlongCell
                          key={cell.id}
                          cell={cell}
                          enableColumnResizing={enableColumnResizing}
                          hasColumnPinning={hasColumnPinning}
                          nonDraggableColumnIds={nonDraggableColumnIds}
                        />
                      ))}
                    </SortableContext>
                  ) : (
                    row.getVisibleCells().map(cell => {
                      const { column } = cell;
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            cell.column.columnDef.meta?.cellClassName
                          )}
                          style={{
                            ...(enableColumnResizing
                              ? { width: cell.column.getSize() }
                              : {}),
                            ...(hasColumnPinning
                              ? getCommonPinningStyles(column)
                              : {}),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })
                  )}
                </TableRow>
                {row.getIsExpanded() && renderSubComponent && (
                  <TableRow key={`${row.id}-expanded`}>
                    <TableCell colSpan={columns.length}>
                      {renderSubComponent(row)}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div
      className={cn('flex flex-col gap-2 min-w-0 w-full min-h-0', className)}
    >
      {displayTopPagination && (
        <div className="flex items-center justify-between px-2">
          {selectedRowsCount > 0 ? (
            <FlexRow alignItems="center" gap="2" h="full" pt="2">
              <Text variant="body-sm" color="tertiary">
                {selectedRowsCount} rows selected
              </Text>
              {isAllRowsSelected ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.toggleAllRowsSelected(false)}
                  className="hidden lg:flex"
                >
                  Clear selection
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => table.toggleAllRowsSelected(true)}
                  className="hidden lg:flex"
                >
                  Select {totalRows} rows in table
                </Button>
              )}
            </FlexRow>
          ) : (
            <div className="h-10"></div>
          )}
          <div className="flex items-center space-x-6 lg:space-x-8 pt-2">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={value => {
                  table.setPageSize(Number(value));
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue
                    placeholder={table.getState().pagination.pageSize}
                  />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 25, 50, 100, 200].map(pageSize => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              {`Page ${table.getState().pagination.pageIndex + 1} of ${table.getPageCount()}`}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="icon"
                className="hidden size-8 lg:flex"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeft />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRight />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="hidden size-8 lg:flex"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRight />
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Custom top horizontal scrollbar - always visible */}
      <div className={cn(stickyScrollbar && 'sticky top-0 z-10 bg-white pb-1')}>
        <CustomScrollbar tableScrollRef={tableScrollRef} />
      </div>

      {enableColumnReordering ? (
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          {tableContent}
        </DndContext>
      ) : (
        tableContent
      )}

      {!hidePagination && (
        <div className="flex items-center justify-end px-2">
          <div className="flex items-center space-x-6 lg:space-x-8 pt-2">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <Select
                value={`${table.getState().pagination.pageSize}`}
                onValueChange={value => {
                  table.setPageSize(Number(value));
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue
                    placeholder={table.getState().pagination.pageSize}
                  />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 25, 50, 100, 200].map(pageSize => (
                    <SelectItem key={pageSize} value={`${pageSize}`}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              {`Page ${table.getState().pagination.pageIndex + 1} of ${table.getPageCount()}`}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="icon"
                className="hidden size-8 lg:flex"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeft />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRight />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="hidden size-8 lg:flex"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRight />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function usePagination({
  initialSize = 10,
  initialIndex = 0,
  manualPagination = true,
}: {
  initialSize?: number;
  initialIndex?: number;
  manualPagination?: boolean;
} = {}) {
  const [paginationState, setPaginationState] = useState({
    pageSize: initialSize,
    pageIndex: initialIndex,
  });
  const { pageSize, pageIndex } = paginationState;

  return {
    pagination: {
      state: paginationState,
      onPaginationChange: setPaginationState,
      manualPagination,
    },
    limit: pageSize,
    page: pageIndex + 1,
  };
}

export const CheckboxHeader = <T,>({
  table,
  ref,
  disabled,
}: {
  table: TableType<T>;
  ref?: Ref<HTMLDivElement | null>;
  disabled?: boolean;
}) => {
  // Get only the rows that can be selected
  const selectableRows = table
    .getRowModel()
    .rows.filter(row => row.getCanSelect());
  const selectedRowsCount = selectableRows.filter(row =>
    row.getIsSelected()
  ).length;
  const selectableRowsCount = selectableRows.length;

  // Determine checkbox state based on selectable rows only
  const isChecked =
    selectableRowsCount > 0 && selectedRowsCount === selectableRowsCount;
  const isIndeterminate =
    selectedRowsCount > 0 && selectedRowsCount < selectableRowsCount;

  const handleChange = () => {
    table.toggleAllPageRowsSelected(!isChecked);
  };

  return (
    <FlexRow alignItems="center" ref={ref} justifyContent="center">
      <Checkbox
        checked={isIndeterminate ? 'indeterminate' : isChecked}
        onCheckedChange={handleChange}
        disabled={disabled || selectableRowsCount === 0}
        aria-label="Select all"
        className="translate-x-[-4px]"
      />
    </FlexRow>
  );
};

export const CheckboxCell = <T,>({
  row,
  text,
  ref,
  disabled,
}: {
  row: Row<T>;
  text?: string;
  ref?: Ref<HTMLDivElement | null>;
  disabled?: boolean;
}) => {
  const handleChange = () => {
    row.toggleSelected(!row.getIsSelected());
  };
  return (
    <FlexRow alignItems="center" gap="2" ref={ref} justifyContent="center">
      <Checkbox
        disabled={disabled}
        checked={row.getIsSelected()}
        onCheckedChange={handleChange}
        aria-label="Select row"
        className="translate-x-[-4px] translate-y-[2px]"
      />
      {text && <Text variant="body-sm">{text}</Text>}
    </FlexRow>
  );
};
